// Sales DTOs — BUILD_SPEC.md §5.7 (customers, customer_prices, sales_orders+lines,
// dispatches+lines), §8.4 steps 1-3 (dispatch a load). Owned by G10 (plan.md) — this
// is the ONLY core file G10 fills; the barrel (schemas/index.ts) already re-exports
// it.
//
// `INVOICING_MODES` / `CONSOLIDATION_PERIODS` / `SALES_ORDER_STATUSES` are defined
// LOCALLY rather than added to `@zebra/core/enums.ts` — this goal's S0 registry rule
// is "touch ONLY packages/db/src/schema/sales.ts (+rls),
// packages/core/src/schemas/sales.ts, services/api/src/modules/sales/,
// services/api/src/pdf/delivery-note.ts, colocated tests"; enums.ts is G2's
// foundation file and out of scope here (same precedent as schemas/stores.ts's
// STORES_ISSUE_STATUSES/TRANSFER_STATUSES). `DISPATCH_STATUSES`/`VAT_TREATMENTS`, by
// contrast, ARE imported from `../enums.js`: those are existing, already-ratified
// core lists (same package, no barrel edit) — importing rather than re-declaring them
// under the same name is required here, not just stylistic: `packages/core/src/
// index.ts` re-exports both `./enums.js` and `./schemas/index.js`, so a second LOCAL
// `export const DISPATCH_STATUSES` in this file would collide with enums.ts's own
// export at that top-level barrel (TS2308) — exactly as schemas/purchasing.ts already
// imports `PRICING_BASES`/`PURCHASE_ORDER_STATUSES`/`VAT_TREATMENTS` rather than
// redeclaring them.
// NOTE: `DISPATCH_STATUSES`/`DispatchStatus` are used below (via `z.enum(...)`) but
// deliberately NOT re-exported from this file — `packages/core/src/index.ts` already
// exports them once via `export * from './enums.js'`; re-exporting the identical
// name again via `export * from './schemas/index.js'` would collide at that barrel
// (TS2308) regardless of the value being the same import. Callers get
// `DISPATCH_STATUSES` from `@zebra/core` (resolved to enums.ts) exactly as before.
import { z } from 'zod';
import { moneyCentsSchema, ulidSchema } from './common.js';
import { DISPATCH_STATUSES, VAT_TREATMENTS } from '../enums.js';
export const INVOICING_MODES = ['PER_LOAD', 'CONSOLIDATED'];
export const CONSOLIDATION_PERIODS = ['WEEKLY', 'MONTHLY'];
export const SALES_ORDER_STATUSES = ['DRAFT', 'OPEN', 'COMPLETE', 'CANCELLED'];
// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
const isActiveQueryParamSchema = z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true'));
/** Query-string pagination (cursor + coerced numeric limit), same shape used by
 * every other module's list endpoint (see schemas/purchasing.ts's matching note). */
const listQueryBaseSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
/** Base UOM quantity, max 3dp, strictly positive — every dispatch/SO line carries the
 * amount to move/sell, always entered as a positive number; the posting endpoint is
 * responsible for choosing the signed direction it hands to `recordMovement` (S1). */
const positiveQtySchema = z
    .string()
    .regex(/^\d+(\.\d{1,3})?$/, 'Invalid decimal quantity (max 3 decimal places)')
    .refine((v) => Number(v) > 0, 'qty must be greater than zero');
/**
 * Cross-field rule for {invoicingMode, consolidationPeriod}: CONSOLIDATED requires a
 * period; PER_LOAD must not carry one (BUILD_SPEC.md §5.7). Attached via
 * `.superRefine` on the containing object schema, mirroring
 * schemas/purchasing.ts's `checkPricingBasis`.
 */
function checkInvoicingMode(val, ctx) {
    if (val.invoicingMode === 'CONSOLIDATED') {
        if (val.consolidationPeriod == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'consolidationPeriod is required for CONSOLIDATED invoicing',
                path: ['consolidationPeriod'],
            });
        }
    }
    else if (val.consolidationPeriod != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'consolidationPeriod must not be set for PER_LOAD invoicing',
            path: ['consolidationPeriod'],
        });
    }
}
/** Cross-field rule for {priceCentsPerTonne, priceCentsPerUnit}: at least one must be
 * set (BUILD_SPEC.md §5.7 "price_cents_per_tonne nullable, price_cents_per_unit
 * nullable"). */
function checkAtLeastOnePrice(val, ctx) {
    if (val.priceCentsPerTonne == null && val.priceCentsPerUnit == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'at least one of priceCentsPerTonne/priceCentsPerUnit is required',
            path: ['priceCentsPerTonne'],
        });
    }
}
// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------
const customerBaseSchema = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    vatNumber: z.string().max(64).nullable().optional(),
    email: z.string().email().nullable().optional(),
    deliveryAddress: z.string().max(500).nullable().optional(),
    paymentTerms: z.string().max(200).nullable().optional(),
    invoicingMode: z.enum(INVOICING_MODES),
    consolidationPeriod: z.enum(CONSOLIDATION_PERIODS).nullable().optional(),
    xeroContactId: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const customerCreateSchema = customerBaseSchema.superRefine(checkInvoicingMode);
const customerUpdateBaseSchema = z.object({
    code: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
    vatNumber: z.string().max(64).nullable().optional(),
    email: z.string().email().nullable().optional(),
    deliveryAddress: z.string().max(500).nullable().optional(),
    paymentTerms: z.string().max(200).nullable().optional(),
    invoicingMode: z.enum(INVOICING_MODES).optional(),
    consolidationPeriod: z.enum(CONSOLIDATION_PERIODS).nullable().optional(),
    xeroContactId: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
});
// Only re-check the mode/period pair when a caller actually touches one of them —
// mirrors schemas/purchasing.ts's supplierContractUpdateSchema.
export const customerUpdateSchema = customerUpdateBaseSchema.superRefine((val, ctx) => {
    if (val.invoicingMode === undefined && val.consolidationPeriod === undefined)
        return;
    if (val.invoicingMode === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'invoicingMode is required when changing consolidationPeriod',
            path: ['invoicingMode'],
        });
        return;
    }
    checkInvoicingMode({ invoicingMode: val.invoicingMode, consolidationPeriod: val.consolidationPeriod }, ctx);
});
export const customerListQuerySchema = listQueryBaseSchema.extend({
    isActive: isActiveQueryParamSchema,
    invoicingMode: z.enum(INVOICING_MODES).optional(),
});
export const customerResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    code: z.string(),
    name: z.string(),
    vatNumber: z.string().nullable(),
    email: z.string().nullable(),
    deliveryAddress: z.string().nullable(),
    paymentTerms: z.string().nullable(),
    invoicingMode: z.enum(INVOICING_MODES),
    consolidationPeriod: z.enum(CONSOLIDATION_PERIODS).nullable(),
    xeroContactId: z.string().nullable(),
    isActive: z.boolean(),
});
// ---------------------------------------------------------------------------
// Customer prices
// ---------------------------------------------------------------------------
const customerPriceBaseSchema = z.object({
    customerId: ulidSchema,
    productId: ulidSchema,
    priceCentsPerTonne: moneyCentsSchema.nullable().optional(),
    priceCentsPerUnit: moneyCentsSchema.nullable().optional(),
    validFrom: z.coerce.date(),
    validTo: z.coerce.date().nullable().optional(),
});
export const customerPriceCreateSchema = customerPriceBaseSchema.superRefine(checkAtLeastOnePrice);
const customerPriceUpdateBaseSchema = z.object({
    customerId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    priceCentsPerTonne: moneyCentsSchema.nullable().optional(),
    priceCentsPerUnit: moneyCentsSchema.nullable().optional(),
    validFrom: z.coerce.date().optional(),
    validTo: z.coerce.date().nullable().optional(),
});
export const customerPriceUpdateSchema = customerPriceUpdateBaseSchema.superRefine((val, ctx) => {
    if (val.priceCentsPerTonne === undefined && val.priceCentsPerUnit === undefined)
        return;
    checkAtLeastOnePrice({ priceCentsPerTonne: val.priceCentsPerTonne ?? null, priceCentsPerUnit: val.priceCentsPerUnit ?? null }, ctx);
});
export const customerPriceListQuerySchema = listQueryBaseSchema.extend({
    customerId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
});
export const customerPriceResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    customerId: ulidSchema,
    productId: ulidSchema,
    priceCentsPerTonne: z.string().nullable(),
    priceCentsPerUnit: z.string().nullable(),
    validFrom: z.coerce.date(),
    validTo: z.coerce.date().nullable(),
});
// ---------------------------------------------------------------------------
// Sales orders + lines
// ---------------------------------------------------------------------------
export const salesOrderLineInputSchema = z.object({
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    qty: positiveQtySchema,
    priceCents: moneyCentsSchema,
});
export const salesOrderCreateSchema = z.object({
    siteId: ulidSchema,
    customerId: ulidSchema,
    reference: z.string().max(200).nullable().optional(),
    notes: z.string().nullable().optional(),
    lines: z.array(salesOrderLineInputSchema).min(1, 'at least one line is required'),
});
export const salesOrderUpdateSchema = z.object({
    siteId: ulidSchema.optional(),
    customerId: ulidSchema.optional(),
    reference: z.string().max(200).nullable().optional(),
    notes: z.string().nullable().optional(),
    lines: z.array(salesOrderLineInputSchema).min(1, 'at least one line is required').optional(),
});
export const salesOrderListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(SALES_ORDER_STATUSES).optional(),
    customerId: ulidSchema.optional(),
});
export const salesOrderLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    qty: z.string(),
    priceCents: z.string(),
});
export const salesOrderResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    number: z.string(),
    customerId: ulidSchema,
    status: z.enum(SALES_ORDER_STATUSES),
    reference: z.string().nullable(),
    notes: z.string().nullable(),
    lines: z.array(salesOrderLineResponseSchema),
});
// ---------------------------------------------------------------------------
// Dispatches + lines
// ---------------------------------------------------------------------------
export const dispatchLineInputSchema = z.object({
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    fromLocationId: ulidSchema,
    qty: positiveQtySchema,
    priceCentsPerUnit: moneyCentsSchema,
    vatTreatment: z.enum(VAT_TREATMENTS),
});
export const dispatchWriteSchema = z.object({
    siteId: ulidSchema,
    customerId: ulidSchema,
    salesOrderId: ulidSchema.nullable().optional(),
    ticketId: ulidSchema.nullable().optional(),
    dispatchedAt: z.coerce.date(),
    notes: z.string().nullable().optional(),
    lines: z.array(dispatchLineInputSchema).min(1, 'at least one line is required'),
});
// Edit (PATCH, DRAFT-only) accepts the exact same shape as create — a full replace
// of the header + lines, matching "documents are freely editable in DRAFT"
// (CLAUDE.md conventions).
export const dispatchUpdateSchema = dispatchWriteSchema;
export const dispatchListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(DISPATCH_STATUSES).optional(),
    customerId: ulidSchema.optional(),
});
export const dispatchLineResponseSchema = z.object({
    id: ulidSchema,
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    fromLocationId: ulidSchema,
    qty: z.string(),
    priceCentsPerUnit: z.string(),
    vatTreatment: z.enum(VAT_TREATMENTS),
    // Populated only once the dispatch has been POSTED (S1/§9.1) — null on a DRAFT line.
    saleValueCents: z.string().nullable(),
    costCents: z.string().nullable(),
    marginCents: z.string().nullable(),
});
export const dispatchResponseSchema = z.object({
    id: ulidSchema,
    tenantId: ulidSchema,
    siteId: ulidSchema,
    number: z.string(),
    customerId: ulidSchema,
    salesOrderId: ulidSchema.nullable(),
    ticketId: z.string().nullable(),
    status: z.enum(DISPATCH_STATUSES),
    dispatchedAt: z.coerce.date(),
    notes: z.string().nullable(),
    deliveryNoteKey: z.string().nullable(),
    deliveryNoteUrl: z.string().nullable(),
    lines: z.array(dispatchLineResponseSchema),
});
/** One `recordMovement` result surfaced back to the caller as part of the posting
 * summary — `valueCents`-derived money transported as decimal strings (JSON has no
 * bigint). Mirrors schemas/stores.ts's storesIssueMovementSummarySchema. */
export const dispatchMovementSummarySchema = z.object({
    lineId: ulidSchema,
    productId: ulidSchema,
    qty: z.string(),
    saleValueCents: z.string(),
    costCents: z.string(),
    marginCents: z.string(),
    movementId: ulidSchema,
});
/** The routing marker G15 (invoicing, later) reads to decide PER_LOAD vs
 * CONSOLIDATED handling — mirrored into the `sales.dispatch.posted` domain_event
 * payload and returned in the POST response for the immediate caller too. */
export const dispatchRoutingSchema = z.object({
    customerId: ulidSchema,
    invoicingMode: z.enum(INVOICING_MODES),
    consolidationPeriod: z.enum(CONSOLIDATION_PERIODS).nullable(),
});
export const dispatchPostResponseSchema = z.object({
    dispatch: dispatchResponseSchema,
    movements: z.array(dispatchMovementSummarySchema),
    routing: dispatchRoutingSchema,
    deliveryNote: z.object({ key: z.string(), url: z.string().optional() }).nullable(),
    summary: z.string(),
});
export const dispatchReverseResponseSchema = z.object({
    dispatch: dispatchResponseSchema,
    reversedMovementIds: z.array(ulidSchema),
});
//# sourceMappingURL=sales.js.map